// crewbit-admin/src/components/money/MarkFiledPanel.tsx
//
// Slide-over form for `mark_remittance_filed`. Operator enters confirmation
// number + collected/itc/owed amounts + optional filed_at + notes; QM rate +
// credit fields surface only when taxMode === 'quick_method'.
//
// Per Decision §Tax.D6 + §3e:
//   - Idempotency replay (same confirmation_number) → toast 'success' with
//     "Already filed — no change" copy + close.
//   - Regular success → toast 'success' "Filed" + close.
//   - Errors → toast 'error' with errorMap copy.
//
// Self-contained — owns its own Toast (matches Session 129 panel pattern).
//
// Spec §Tax.7.2 + §Tax.D6.

import { useState } from 'react';
import { palette, spacing, radii } from '../../styles/tokens';
import { SlidePanel } from '../common/SlidePanel';
import { Toast } from '../common/Toast';
import { MoneyInput } from '../common/MoneyInput';
import { DatePicker } from '../common/DatePicker';
import { canonicalMoney, parseMoney } from '../../lib/money';
import { lookupErrorCopy } from '../../lib/errorMap';
import { markRemittanceFiled, type TaxType } from '../../lib/taxMutations';

interface MarkFiledPanelProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string | null;
  taxType: TaxType;
  periodStart: string;
  periodEnd: string;
  /** Display label, e.g. "Q1 2026". */
  periodLabel: string;
  /** When 'quick_method', QM rate + credit fields render. */
  taxMode: 'charge' | 'exempt' | 'quick_method';
  /** Pre-fill amounts from useTaxSummaryDetail (optional). */
  prefill?: {
    amountCollected?: number | null;
    amountItc?: number | null;
    amountOwed?: number | null;
    quickMethodRemittanceRate?: number | null;
    quickMethodCredit?: number | null;
  };
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: palette.g600,
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: radii.sm,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  fontFamily: 'inherit',
  fontSize: 13.5,
  color: palette.g800,
  outline: 'none',
  boxSizing: 'border-box',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 72,
  resize: 'vertical',
  fontFamily: 'inherit',
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function MarkFiledPanel({
  isOpen,
  onClose,
  businessId,
  taxType,
  periodStart,
  periodEnd,
  periodLabel,
  taxMode,
  prefill,
}: MarkFiledPanelProps) {
  const [confirmation, setConfirmation] = useState('');
  const [amountCollected, setAmountCollected] = useState(canonicalMoney(prefill?.amountCollected ?? 0));
  const [amountItc, setAmountItc] = useState(canonicalMoney(prefill?.amountItc ?? 0));
  const [amountOwed, setAmountOwed] = useState(canonicalMoney(prefill?.amountOwed ?? 0));
  const [qmRate, setQmRate] = useState<string>(
    prefill?.quickMethodRemittanceRate != null ? String(prefill.quickMethodRemittanceRate) : '',
  );
  const [qmCredit, setQmCredit] = useState<string>(canonicalMoney(prefill?.quickMethodCredit ?? 0));
  const [filedAt, setFiledAt] = useState<string | null>(todayIso());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const isQuickMethod = taxMode === 'quick_method';
  const canSubmit = !!businessId && confirmation.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!businessId || !canSubmit) return;
    setSubmitting(true);
    const result = await markRemittanceFiled({
      businessId,
      taxType,
      periodStart,
      periodEnd,
      confirmationNumber: confirmation.trim(),
      amountCollected: Number(parseMoney(amountCollected)),
      amountItc: Number(parseMoney(amountItc)),
      amountOwed: Number(parseMoney(amountOwed)),
      filedAt: filedAt ? new Date(filedAt).toISOString() : null,
      quickMethodRemittanceRate: isQuickMethod && qmRate.trim() ? Number(qmRate) : null,
      quickMethodCredit: isQuickMethod ? Number(parseMoney(qmCredit)) : null,
      notes: notes.trim() || null,
    });
    setSubmitting(false);

    if (result.success) {
      setToast({
        msg: result.data.already_filed ? 'Already filed — no change.' : 'Filed.',
        type: 'success',
      });
      // Close after the toast becomes visible so the operator sees confirmation.
      setTimeout(onClose, 250);
    } else {
      setToast({
        msg: lookupErrorCopy('mark_remittance_filed', result.error.code),
        type: 'error',
      });
    }
  };

  return (
    <>
      <SlidePanel
        isOpen={isOpen}
        onClose={onClose}
        title="Mark filing"
        subtitle={`${taxType.toUpperCase()} · ${periodLabel}`}
        width={720}
      >
        <div style={{ display: 'grid', gap: spacing.md }}>
          <div>
            <label style={labelStyle} htmlFor="mf-confirmation">
              Confirmation number
            </label>
            <input
              id="mf-confirmation"
              type="text"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder="e.g. CRA12345678"
              style={inputStyle}
              autoFocus
            />
            <div style={{ fontSize: 11.5, color: palette.g500, marginTop: 4 }}>
              From your CRA filing receipt. Used to detect replays — re-filing
              with the same number is a no-op.
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: spacing.sm }}>
            <div>
              <label style={labelStyle}>Collected</label>
              <MoneyInput value={amountCollected} onChange={setAmountCollected} ariaLabel="Amount collected" />
            </div>
            <div>
              <label style={labelStyle}>ITCs</label>
              <MoneyInput value={amountItc} onChange={setAmountItc} ariaLabel="ITCs" />
            </div>
            <div>
              <label style={labelStyle}>Owed</label>
              <MoneyInput value={amountOwed} onChange={setAmountOwed} ariaLabel="Amount owed" />
            </div>
          </div>

          {isQuickMethod && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.sm }}>
              <div>
                <label style={labelStyle} htmlFor="mf-qmrate">QM remittance rate</label>
                <input
                  id="mf-qmrate"
                  type="text"
                  inputMode="decimal"
                  value={qmRate}
                  onChange={(e) => setQmRate(e.target.value)}
                  placeholder="0.088"
                  style={inputStyle}
                />
                <div style={{ fontSize: 11.5, color: palette.g500, marginTop: 4 }}>
                  Decimal (e.g. 0.088 = 8.8%).
                </div>
              </div>
              <div>
                <label style={labelStyle}>QM credit</label>
                <MoneyInput value={qmCredit} onChange={setQmCredit} ariaLabel="Quick method credit" />
              </div>
            </div>
          )}

          <div>
            <label style={labelStyle}>Filed on</label>
            <DatePicker
              value={filedAt}
              onChange={setFiledAt}
              maxDate={todayIso()}
              clearable={false}
            />
          </div>

          <div>
            <label style={labelStyle} htmlFor="mf-notes">Notes</label>
            <textarea
              id="mf-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional. e.g. payment method, accountant signoff."
              style={textareaStyle}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: spacing.sm,
              marginTop: spacing.md,
              paddingTop: spacing.md,
              borderTop: `1px solid ${palette.g100}`,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '9px 16px',
                borderRadius: radii.sm,
                border: `1px solid ${palette.g200}`,
                background: palette.white,
                color: palette.g700,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{
                padding: '9px 18px',
                borderRadius: radii.sm,
                border: 'none',
                background: canSubmit ? palette.accent : palette.g300,
                color: palette.white,
                fontSize: 13,
                fontWeight: 600,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit',
              }}
            >
              {submitting ? 'Filing…' : 'Mark as filed'}
            </button>
          </div>
        </div>
      </SlidePanel>
      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          isVisible={!!toast}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}
